/**
 * Campaign Preview Upload Service
 *
 * Handles uploading campaign preview screenshots to Supabase Storage
 * and managing preview image lifecycle (cleanup of old previews)
 *
 * Storage Path: Email-Campaign-Previews/{campaignId}_preview_{timestamp}.webp
 *
 * @created 2025-10-30
 */

import { supabase } from '@/integrations/supabase/client';

/**
 * Upload campaign preview screenshot to Supabase Storage
 *
 * Creates a timestamped preview image in the Email-Campaign-Previews folder
 * and returns the public URL for storage in the database.
 *
 * @param campaignId - Campaign UUID
 * @param imageBlob - Processed WebP image blob
 * @returns Promise<string> - Public URL of uploaded image
 * @throws Error if upload fails
 *
 * @example
 * const blob = await capturePreviewDialogScreenshot();
 * const processed = await processTemplatePreview(blob);
 * const url = await uploadCampaignPreview(campaignId, processed);
 */
export const uploadCampaignPreview = async (
  campaignId: string,
  imageBlob: Blob
): Promise<string> => {
  try {
    console.log('[uploadCampaignPreview] Starting upload for campaign:', campaignId);
    console.log('[uploadCampaignPreview] Image size:', (imageBlob.size / 1024).toFixed(2), 'KB');

    // Generate filename with timestamp
    const timestamp = Date.now();
    const filename = `${campaignId}_preview_${timestamp}.webp`;
    const path = `Email-Campaign-Previews/${filename}`;

    console.log('[uploadCampaignPreview] Upload path:', path);

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('images')
      .upload(path, imageBlob, {
        contentType: 'image/webp',
        cacheControl: '3600', // Cache for 1 hour
        upsert: true // Replace if exists (unlikely with timestamp)
      });

    if (error) {
      console.error('[uploadCampaignPreview] Upload error:', error);
      throw new Error(`Upload failed: ${error.message}`);
    }

    console.log('[uploadCampaignPreview] Upload successful:', data.path);

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('images')
      .getPublicUrl(path);

    const publicUrl = urlData.publicUrl;
    console.log('[uploadCampaignPreview] Public URL:', publicUrl);

    return publicUrl;

  } catch (error) {
    console.error('[uploadCampaignPreview] Exception:', error);
    throw new Error(
      `Failed to upload campaign preview: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
};

/**
 * Delete old campaign preview images
 *
 * Keeps only the most recent preview for a campaign and removes all others.
 * This prevents storage bloat when users capture multiple previews.
 *
 * Non-blocking operation - errors are logged but not thrown.
 *
 * @param campaignId - Campaign UUID
 * @returns Promise<void>
 *
 * @example
 * // After successful upload and database update
 * deleteOldCampaignPreviews(campaignId).catch(err => {
 *   console.warn('Cleanup failed (non-critical):', err);
 * });
 */
export const deleteOldCampaignPreviews = async (
  campaignId: string
): Promise<void> => {
  try {
    console.log('[deleteOldCampaignPreviews] Cleaning up old previews for campaign:', campaignId);

    // List all preview files for this campaign
    const { data: files, error: listError } = await supabase.storage
      .from('images')
      .list('Email-Campaign-Previews/', {
        search: `${campaignId}_preview_`
      });

    if (listError) {
      console.warn('[deleteOldCampaignPreviews] List error:', listError);
      return;
    }

    if (!files || files.length === 0) {
      console.log('[deleteOldCampaignPreviews] No preview files found');
      return;
    }

    console.log('[deleteOldCampaignPreviews] Found', files.length, 'preview file(s)');

    // Sort files by timestamp (newest first)
    // Filename format: {campaignId}_preview_{timestamp}.webp
    const sortedFiles = files
      .sort((a, b) => {
        // Extract timestamp from filename
        const aTimestamp = parseInt(a.name.split('_preview_')[1]?.split('.')[0] || '0');
        const bTimestamp = parseInt(b.name.split('_preview_')[1]?.split('.')[0] || '0');
        return bTimestamp - aTimestamp; // Descending order (newest first)
      });

    // Keep the newest file, delete the rest
    const filesToDelete = sortedFiles
      .slice(1) // Skip first (newest)
      .map(f => `Email-Campaign-Previews/${f.name}`);

    if (filesToDelete.length === 0) {
      console.log('[deleteOldCampaignPreviews] Only one preview exists, nothing to clean up');
      return;
    }

    console.log('[deleteOldCampaignPreviews] Deleting', filesToDelete.length, 'old preview(s)');

    // Delete old files
    const { error: deleteError } = await supabase.storage
      .from('images')
      .remove(filesToDelete);

    if (deleteError) {
      console.warn('[deleteOldCampaignPreviews] Delete failed (non-critical):', deleteError);
    } else {
      console.log('[deleteOldCampaignPreviews] Cleanup successful');
    }

  } catch (error) {
    // Non-critical operation - log but don't throw
    console.warn('[deleteOldCampaignPreviews] Exception (non-critical):', error);
  }
};

/**
 * Delete all previews for a campaign
 *
 * Used when a campaign is deleted. Removes all preview images
 * associated with the campaign from storage.
 *
 * @param campaignId - Campaign UUID
 * @returns Promise<void>
 *
 * @example
 * // In campaign deletion handler
 * await deleteCampaign(campaignId);
 * await deleteAllCampaignPreviews(campaignId);
 */
export const deleteAllCampaignPreviews = async (
  campaignId: string
): Promise<void> => {
  try {
    console.log('[deleteAllCampaignPreviews] Deleting all previews for campaign:', campaignId);

    // List all preview files for this campaign
    const { data: files, error: listError } = await supabase.storage
      .from('images')
      .list('Email-Campaign-Previews/', {
        search: `${campaignId}_preview_`
      });

    if (listError || !files || files.length === 0) {
      console.log('[deleteAllCampaignPreviews] No preview files to delete');
      return;
    }

    // Build full paths for deletion
    const filesToDelete = files.map(f => `Email-Campaign-Previews/${f.name}`);

    console.log('[deleteAllCampaignPreviews] Deleting', filesToDelete.length, 'preview file(s)');

    // Delete all files
    const { error: deleteError } = await supabase.storage
      .from('images')
      .remove(filesToDelete);

    if (deleteError) {
      console.warn('[deleteAllCampaignPreviews] Delete failed:', deleteError);
    } else {
      console.log('[deleteAllCampaignPreviews] All previews deleted');
    }

  } catch (error) {
    console.warn('[deleteAllCampaignPreviews] Exception:', error);
  }
};

/**
 * Get preview URL from database record
 *
 * Helper to extract preview URL from campaign object
 *
 * @param campaign - Campaign object with preview_image_url field
 * @returns string | null - Preview URL or null if not set
 */
export const getCampaignPreviewUrl = (
  campaign: { preview_image_url?: string | null }
): string | null => {
  return campaign.preview_image_url || null;
};

/**
 * Check if campaign has a preview
 *
 * @param campaign - Campaign object
 * @returns boolean - True if preview URL exists
 */
export const hasCampaignPreview = (
  campaign: { preview_image_url?: string | null }
): boolean => {
  return !!campaign.preview_image_url;
};
